"use client";

import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import type { ChordQuality } from "@/lib/virtuosa-layout/types";

export interface HintNote {
  midi: number;
  /** RGB color (e.g. 0x8b5cf6). */
  color: number;
  role?: "root" | "note";
}

// Unified hint published by any feature that wants to highlight notes.
// Multiple consumers subscribe (studio piano, FAB pill, …); each reads what
// it needs from the shape (midis for the piano, chord/voicing for placement
// on the FAB pill).
export interface Hint {
  /**
   * Stable identifier for the producer. One slot per source: re-publishing
   * with the same source replaces the previous entry in place.
   */
  source: string;
  /**
   * Optional abstract chord identity. Used by the FAB pill via placeChord
   * to highlight the canonical root-position keyboard mapping. Falls back
   * here when no explicit voicing is provided.
   */
  chord?: { rootSemi: number; quality: ChordQuality };
  /**
   * Optional explicit voicing, the exact MIDI notes the user is meant to
   * play right now (e.g. an inversion of `chord`, or a custom voicing).
   * When present, the FAB pill prefers this over `chord` so the keys it
   * shows reflect the targeted voicing rather than the abstract chord.
   */
  voicing?: number[];
  midis: HintNote[];
  label?: string;
}

interface HintContextValue {
  current: Hint | null;
  publish: (hint: Hint) => void;
  retract: (source: string) => void;
}

const HintContext = createContext<HintContextValue | null>(null);

export function HintProvider({ children }: { children: ReactNode }) {
  // Stack of active hints, keyed by source. Insertion order = mount order;
  // re-publishing moves the source to the top so the most recent activity
  // wins. The provider exposes `current` = top of the stack.
  const stackRef = useRef<Map<string, Hint>>(new Map());
  const [current, setCurrent] = useState<Hint | null>(null);

  const recompute = useCallback(() => {
    const stack = stackRef.current;
    if (stack.size === 0) {
      setCurrent(null);

      return;
    }

    let last: Hint | null = null;
    for (const h of stack.values()) {
      last = h;
    }

    setCurrent(last);
  }, []);

  const publish = useCallback(
    (hint: Hint) => {
      const stack = stackRef.current;
      stack.delete(hint.source);
      stack.set(hint.source, hint);
      recompute();
    },
    [recompute],
  );

  const retract = useCallback(
    (source: string) => {
      const stack = stackRef.current;
      if (!stack.delete(source)) {
        return;
      }

      recompute();
    },
    [recompute],
  );

  const value = useMemo<HintContextValue>(
    () => ({ current, publish, retract }),
    [current, publish, retract],
  );

  return <HintContext.Provider value={value}>{children}</HintContext.Provider>;
}

export function useHintContext(): HintContextValue {
  const ctx = useContext(HintContext);

  if (!ctx) {
    throw new Error("useHintContext must be used within <HintProvider>");
  }

  return ctx;
}

/**
 * Read the current hint (top of the stack). Returns null when no producer
 * is publishing.
 */
export function useCurrentHint(): Hint | null {
  return useHintContext().current;
}
